import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { 
  Upload, X, Check, RefreshCw, ChevronRight, ChevronLeft,
  FileSpreadsheet, Table, Eye, Save, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Column letters A-Z, AA-AZ for reference
const generateColumnLetters = (count) => {
  const letters = [];
  for (let i = 0; i < count; i++) {
    if (i < 26) {
      letters.push(String.fromCharCode(65 + i));
    } else {
      letters.push('A' + String.fromCharCode(65 + (i - 26)));
    }
  }
  return letters;
};

const CatalogWizardModal = ({ projectId, projectName, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [parsedConcepts, setParsedConcepts] = useState(null);
  
  // Column mapping state
  const [mapping, setMapping] = useState({
    description_col: '',
    unit_col: '',
    quantity_col: '',
    unit_price_col: '',
    key_col: '',
    area_col: '',
    partida_col: '',
    start_row: 2
  });

  // STEP 1: Upload file and get preview
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.xlsx')) {
      toast.error('Solo se aceptan archivos .xlsx');
      return;
    }
    
    setFile(selectedFile);
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API}/catalog/preview-excel/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPreviewData(response.data);
      toast.success('Archivo cargado correctamente');
      
      // Try to auto-detect columns based on common headers
      autoDetectColumns(response.data);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar archivo');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-detect columns based on common header names
  const autoDetectColumns = (data) => {
    if (!data?.preview_rows?.length) return;
    
    // Check first 3 rows for headers
    const headerRows = data.preview_rows.slice(0, 3);
    
    const detectedMapping = { ...mapping };
    
    for (const row of headerRows) {
      for (const cell of row.cells) {
        const value = cell.value.toLowerCase();
        const col = cell.column;
        
        // Description
        if (!detectedMapping.description_col && 
            (value.includes('descripci') || value.includes('concepto') || value.includes('description'))) {
          detectedMapping.description_col = col;
        }
        
        // Unit
        if (!detectedMapping.unit_col && 
            (value === 'unidad' || value === 'unit' || value === 'u.m.' || value === 'um')) {
          detectedMapping.unit_col = col;
        }
        
        // Quantity
        if (!detectedMapping.quantity_col && 
            (value.includes('cantidad') || value.includes('qty') || value.includes('quantity') || value === 'cant.')) {
          detectedMapping.quantity_col = col;
        }
        
        // Unit price
        if (!detectedMapping.unit_price_col && 
            (value.includes('precio') || value.includes('p.u.') || value.includes('unit price') || value === 'pu')) {
          detectedMapping.unit_price_col = col;
        }
        
        // Key
        if (!detectedMapping.key_col && 
            (value === 'clave' || value === 'key' || value === 'código' || value === 'codigo' || value === 'no.')) {
          detectedMapping.key_col = col;
        }
        
        // Área / Partida (opcionales)
        if (!detectedMapping.area_col && (value === 'área' || value === 'area' || value === 'zona' || value === 'ubicación' || value === 'ubicacion')) {
          detectedMapping.area_col = col;
        }
        if (!detectedMapping.partida_col && (value === 'partida' || value === 'capítulo' || value === 'capitulo' || value === 'sección' || value === 'seccion')) {
          detectedMapping.partida_col = col;
        }
      }
    }
    
    // Guess start_row (first row after headers)
    let startRow = 2;
    for (let i = 0; i < Math.min(data.preview_rows.length, 10); i++) {
      const row = data.preview_rows[i];
      // Check if this row has numeric values in quantity column
      if (detectedMapping.quantity_col) {
        const qtyCell = row.cells.find(c => c.column === detectedMapping.quantity_col);
        if (qtyCell && !isNaN(parseFloat(qtyCell.value))) {
          startRow = row.row_number;
          break;
        }
      }
    }
    detectedMapping.start_row = startRow;
    
    setMapping(detectedMapping);
  };

  // STEP 2: Parse with mapping
  const handleParseWithMapping = async () => {
    if (!mapping.description_col || !mapping.unit_col || !mapping.quantity_col || !mapping.unit_price_col) {
      toast.error('Por favor mapea todas las columnas requeridas');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      
      const response = await axios.post(
        `${API}/catalog/parse-with-mapping/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.total_concepts === 0) {
        toast.error('No se encontraron conceptos válidos con este mapeo');
        return;
      }
      
      setParsedConcepts(response.data);
      setStep(3);
      toast.success(`${response.data.total_concepts} conceptos encontrados`);
      
    } catch (error) {
      console.error('Error parsing:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    } finally {
      setLoading(false);
    }
  };

  // STEP 3: Upload to database
  const handleConfirmUpload = async () => {
    if (!parsedConcepts?.all_concepts?.length) {
      toast.error('No hay conceptos para cargar');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/catalog/upload/${projectId}`, {
        concepts: parsedConcepts.all_concepts
      });
      
      toast.success(`Catálogo cargado: ${parsedConcepts.all_concepts.length} conceptos`);
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar catálogo');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (field, value) => {
    setMapping(prev => ({ ...prev, [field]: value }));
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3].map(s => (
        <div key={s} className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
            s === step ? 'bg-[#FF6B00] text-white' : 
            s < step ? 'bg-[#22C55E] text-white' : 'bg-[#1E3550] text-[#5A7A9A]'
          }`}>
            {s < step ? <Check className="w-4 h-4" /> : s}
          </div>
          {s < 3 && (
            <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-[#22C55E]' : 'bg-[#1E3550]'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="catalog-wizard-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-[#00CFFF]">Cargar Catálogo</h2>
            <p className="text-[#5A7A9A] text-sm">{projectName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStepIndicator()}
          
          {/* STEP 1: Upload File */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-[#E2EAF4] mb-2">
                  Paso 1: Seleccionar Archivo Excel
                </h3>
                <p className="text-[#5A7A9A] text-sm">
                  Sube el catálogo de conceptos en formato .xlsx
                </p>
              </div>

              {/* Upload Zone */}
              <div className="border-2 border-dashed border-[#1E3550] rounded-xl p-8 text-center bg-[#0A1422]">
                <FileSpreadsheet className="w-16 h-16 text-[#5A7A9A] mx-auto mb-4" />
                <p className="text-[#E2EAF4] mb-4">
                  {file ? file.name : 'Arrastra un archivo o haz clic para seleccionar'}
                </p>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="excel-file"
                  disabled={loading}
                />
                <label htmlFor="excel-file">
                  <Button variant="outline" disabled={loading} asChild className="border-[#1E3550] text-[#E2EAF4] hover:bg-[#1E3550]">
                    <span className="cursor-pointer">
                      {loading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {file ? 'Cambiar archivo' : 'Seleccionar archivo'}
                    </span>
                  </Button>
                </label>
              </div>

              {/* Preview Table */}
              {previewData && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Table className="w-5 h-5 text-[#5A7A9A]" />
                    <h4 className="font-semibold text-[#E2EAF4]">
                      Vista previa del archivo ({previewData.total_rows} filas, {previewData.total_columns} columnas)
                    </h4>
                  </div>
                  <div className="border border-[#1E3550] rounded-lg overflow-x-auto max-h-64">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#0A1422] sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">#</th>
                          {previewData.columns.map(col => (
                            <th key={col} className="px-3 py-2 text-center font-medium text-[#5A7A9A] border-b border-[#1E3550] min-w-[80px]">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview_rows.map((row, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]'}>
                            <td className="px-3 py-2 text-[#5A7A9A] border-b border-[#1E3550] font-mono text-xs">{row.row_number}</td>
                            {row.cells.map((cell, cidx) => (
                              <td key={cidx} className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4] truncate max-w-[150px]" title={cell.value}>
                                {cell.value || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Map Columns */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-[#E2EAF4] mb-2">
                  Paso 2: Mapear Columnas
                </h3>
                <p className="text-[#5A7A9A] text-sm">
                  Indica en qué columna está cada campo
                </p>
              </div>

              <div className="bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-[#E2EAF4]">
                  <p className="font-medium">Selecciona las columnas que correspondan a cada campo.</p>
                  <p className="mt-1 text-[#5A7A9A]">Los campos marcados con * son obligatorios.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Description Column */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Descripción / Concepto *
                  </label>
                  <select
                    value={mapping.description_col}
                    onChange={(e) => handleMappingChange('description_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-description-col"
                  >
                    <option value="">Seleccionar columna...</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Unit Column */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Unidad *
                  </label>
                  <select
                    value={mapping.unit_col}
                    onChange={(e) => handleMappingChange('unit_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-unit-col"
                  >
                    <option value="">Seleccionar columna...</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Quantity Column */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Cantidad *
                  </label>
                  <select
                    value={mapping.quantity_col}
                    onChange={(e) => handleMappingChange('quantity_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-quantity-col"
                  >
                    <option value="">Seleccionar columna...</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Unit Price Column */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Precio Unitario *
                  </label>
                  <select
                    value={mapping.unit_price_col}
                    onChange={(e) => handleMappingChange('unit_price_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-price-col"
                  >
                    <option value="">Seleccionar columna...</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Key Column (optional) */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Clave (opcional)
                  </label>
                  <select
                    value={mapping.key_col}
                    onChange={(e) => handleMappingChange('key_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-key-col"
                  >
                    <option value="">Sin clave</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Área (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Área <span className="text-[#5A7A9A]">(opcional · zona/edificio)</span>
                  </label>
                  <select
                    value={mapping.area_col}
                    onChange={(e) => handleMappingChange('area_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-area-col"
                  >
                    <option value="">Sin área</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Partida (opcional) */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Partida <span className="text-[#5A7A9A]">(opcional · tipo de trabajo)</span>
                  </label>
                  <select
                    value={mapping.partida_col}
                    onChange={(e) => handleMappingChange('partida_col', e.target.value)}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] focus:ring-2 focus:ring-[#0066FF]"
                    data-testid="select-partida-col"
                  >
                    <option value="">Sin partida</option>
                    {previewData?.columns.map(col => (
                      <option key={col} value={col}>Columna {col}</option>
                    ))}
                  </select>
                </div>

                {/* Start Row */}
                <div>
                  <label className="block text-sm font-medium text-[#E2EAF4] mb-1">
                    Los datos inician en la fila:
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={mapping.start_row}
                    onChange={(e) => handleMappingChange('start_row', parseInt(e.target.value) || 1)}
                    className="w-full bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-start-row"
                  />
                </div>
              </div>

              {/* Mini Preview with Mapping */}
              {previewData && mapping.description_col && (
                <div className="mt-4">
                  <h4 className="font-medium text-[#E2EAF4] mb-2">Vista previa con mapeo:</h4>
                  <div className="border border-[#1E3550] rounded-lg overflow-x-auto max-h-40">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#0A1422]">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Fila</th>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Clave</th>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Descripción</th>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Unidad</th>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Cantidad</th>
                          <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">P.U.</th>
                          {(mapping.area_col || mapping.partida_col) && (
                            <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Área / Partida</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview_rows
                          .filter(row => row.row_number >= mapping.start_row)
                          .slice(0, 5)
                          .map((row, idx) => {
                            const getCell = (col) => row.cells.find(c => c.column === col)?.value || '-';
                            return (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]'}>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#5A7A9A]">{row.row_number}</td>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4]">{getCell(mapping.key_col)}</td>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4] truncate max-w-[200px]">{getCell(mapping.description_col)}</td>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4]">{getCell(mapping.unit_col)}</td>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4]">{getCell(mapping.quantity_col)}</td>
                                <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4]">{getCell(mapping.unit_price_col)}</td>
                                {(mapping.area_col || mapping.partida_col) && (
                                  <td className="px-3 py-2 border-b border-[#1E3550] text-[#00CFFF]">{[mapping.area_col && getCell(mapping.area_col), mapping.partida_col && getCell(mapping.partida_col)].filter(v => v && v !== '-').join(' · ') || '-'}</td>
                                )}
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Preview Concepts */}
          {step === 3 && parsedConcepts && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-[#E2EAF4] mb-2">
                  Paso 3: Confirmar Conceptos
                </h3>
                <p className="text-[#5A7A9A] text-sm">
                  Revisa los conceptos encontrados antes de guardar
                </p>
              </div>

              {/* Summary */}
              <div className="bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#22C55E]">
                    {parsedConcepts.total_concepts} conceptos encontrados
                  </p>
                  <p className="text-sm text-[#5A7A9A]">
                    Total contratado: ${parsedConcepts.total_contratado?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <Check className="w-8 h-8 text-[#22C55E]" />
              </div>

              {/* Concepts Preview */}
              <div>
                <h4 className="font-medium text-[#E2EAF4] mb-2">
                  Primeros {Math.min(15, parsedConcepts.preview?.length || 0)} conceptos:
                </h4>
                <div className="border border-[#1E3550] rounded-lg overflow-x-auto max-h-64">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#0A1422] sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">#</th>
                        <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Clave</th>
                        <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Descripción</th>
                        <th className="px-3 py-2 text-left font-medium text-[#5A7A9A] border-b border-[#1E3550]">Unidad</th>
                        <th className="px-3 py-2 text-right font-medium text-[#5A7A9A] border-b border-[#1E3550]">Cantidad</th>
                        <th className="px-3 py-2 text-right font-medium text-[#5A7A9A] border-b border-[#1E3550]">P.U.</th>
                        <th className="px-3 py-2 text-right font-medium text-[#5A7A9A] border-b border-[#1E3550]">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedConcepts.preview?.map((concept, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-[#0D1520]' : 'bg-[#0A1422]'}>
                          <td className="px-3 py-2 border-b border-[#1E3550] text-[#5A7A9A]">{idx + 1}</td>
                          <td className="px-3 py-2 border-b border-[#1E3550] font-mono text-xs text-[#00CFFF]">
                            {concept.clave || '-'}
                            {(concept.area || concept.partida) && (
                              <span className="block text-[9px] font-sans text-[#5A7A9A]">{[concept.area, concept.partida].filter(Boolean).join(' · ')}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 border-b border-[#1E3550] truncate max-w-[250px] text-[#E2EAF4]" title={concept.descripcion}>
                            {concept.descripcion}
                          </td>
                          <td className="px-3 py-2 border-b border-[#1E3550] text-[#E2EAF4]">{concept.unidad}</td>
                          <td className="px-3 py-2 border-b border-[#1E3550] text-right font-mono text-[#E2EAF4]">
                            {concept.cantidad_contratada?.toLocaleString('es-MX')}
                          </td>
                          <td className="px-3 py-2 border-b border-[#1E3550] text-right font-mono text-[#E2EAF4]">
                            ${concept.precio_unitario?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2 border-b border-[#1E3550] text-right font-mono font-semibold text-[#22C55E]">
                            ${concept.importe?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedConcepts.total_concepts > 15 && (
                  <p className="text-sm text-[#5A7A9A] mt-2">
                    ... y {parsedConcepts.total_concepts - 15} conceptos más
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-between flex-shrink-0 bg-[#0A1422]">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose} className="text-[#E2EAF4] hover:bg-[#1E3550]">Cancelar</Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!previewData || loading}
                className="bg-[#0066FF] hover:bg-[#0066FF]/80 text-white"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : step === 2 ? (
            <>
              <Button variant="ghost" onClick={() => setStep(1)} className="text-[#E2EAF4] hover:bg-[#1E3550]">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
              <Button 
                onClick={handleParseWithMapping}
                disabled={loading || !mapping.description_col || !mapping.unit_col || !mapping.quantity_col || !mapping.unit_price_col}
                className="bg-[#0066FF] hover:bg-[#0066FF]/80 text-white"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Ver Conceptos
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(2)} className="text-[#E2EAF4] hover:bg-[#1E3550]">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Cambiar Mapeo
              </Button>
              <Button 
                onClick={handleConfirmUpload}
                disabled={loading}
                className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-white"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Confirmar Carga
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogWizardModal;
